import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";


interface WritePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (post: { title: string; content: string; category: string }) => Promise<{ ok: boolean; error?: string }>;
}

const categories: { value: string; label: string }[] = [
  { value: "자유수다", label: "자유수다" },
  { value: "물건 Q&A", label: "물건 Q&A" },
  { value: "낙찰/임장 후기", label: "낙찰/임장 후기" },
];

export default function WritePostModal({ isOpen, onClose, onSubmit }: WritePostModalProps) {
  const [category, setCategory] = useState<string>("자유수다");
  const [submitError, setSubmitError] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [errors, setErrors] = useState<{ title?: string; content?: string }>();

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const newErrors: { title?: string; content?: string } = {};
    if (!title.trim()) newErrors.title = "제목을 입력해주세요";
    if (!content.trim()) newErrors.content = "내용을 입력해주세요";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    const res = await onSubmit({ title: title.trim(), content: content.trim(), category });
    setSubmitting(false);
    if (!res.ok) {
      setSubmitError(res.error || "등록에 실패했습니다.");
      return;
    }
    setTitle("");
    setContent("");
    setCategory("자유수다");
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          ></motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative bg-white rounded-2xl max-w-[560px] w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground-950">글쓰기</h2>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Category */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground-700 mb-2">카테고리</label>
                <div className="flex items-center gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat.value)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-all whitespace-nowrap ${
                        category === cat.value
                          ? "bg-foreground-950 text-white border-foreground-950"
                          : "bg-white text-foreground-600 border-foreground-200 hover:border-foreground-400"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="제목을 입력하세요"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: undefined })); }}
                  className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none transition-colors ${
                    errors.title ? "border-red-300 focus:border-red-400" : "border-foreground-200 focus:border-accent-400"
                  }`}
                />
                {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
              </div>

              {/* Content */}
              <div className="mb-2">
                <textarea
                  placeholder="내용을 입력하세요"
                  value={content}
                  onChange={(e) => { setContent(e.target.value); setErrors((p) => ({ ...p, content: undefined })); }}
                  rows={6}
                  className={`w-full px-4 py-3 text-sm border rounded-lg bg-white focus:outline-none transition-colors resize-none ${
                    errors.content ? "border-red-300 focus:border-red-400" : "border-foreground-200 focus:border-accent-400"
                  }`}
                ></textarea>
                {errors.content && <p className="mt-1 text-xs text-red-500">{errors.content}</p>}
              </div>

              {category === "물건 Q&A" && (
                <div className="mb-4 p-3 rounded-xl bg-accent-50 border border-accent-200 flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-accent-200 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-robot-2-line text-xs text-accent-700"></i>
                  </div>
                  <p className="text-xs text-accent-800 leading-relaxed">
                    AI 경매비서가 1분 내로 1차 권리분석 답변을 달아드립니다
                  </p>
                </div>
              )}

              {submitError && (
                <p className="mb-2 text-xs text-red-600">{submitError}</p>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full py-2.5 rounded-full bg-foreground-950 text-white text-sm font-medium hover:bg-foreground-800 transition-all disabled:opacity-50"
              >
                {submitting ? "등록 중…" : "등록하기"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}